<template>
    <div>
        <div class="debug-info-panel" :class="{'pinned': pinned}" v-if="showDebugPanel">
            <div class="panel-header mb-2">
                <div><v-icon>mdi-bug</v-icon> Debug Panel</div>
                <v-btn icon @click="pinned = !pinned">
                    <v-icon>{{ !pinned ? 'mdi-pin-off' : 'mdi-pin'}}</v-icon>
                </v-btn>
            </div>

            <div class="debug-info-panel-body">
                <v-expansion-panels>
                    <v-expansion-panel>
                        <v-expansion-panel-header>
                            Tribute Video State
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <div class="header-section mb-1" v-if="state.isFamily || state.isContributor">
                                <v-chip v-if="state.isFamily">
                                    <v-badge inline dot :color="state.isFamily ? 'success' : 'error'"/>
                                    is Family
                                </v-chip>
                                <v-chip v-if="state.isContributor">
                                    <v-badge inline dot :color="state.isContributor ? 'success' : 'error'"/>
                                    is Contributor
                                </v-chip>
                                <v-chip>
                                    <v-badge inline dot :color="state.anonUserDetails ? 'success' : 'error'"/>
                                    User details set
                                </v-chip>
                            </div>
                            <div class="debug-section gap" v-if="$store.state.tributeVideo">
                                <!-- Tribute Video -->
                                <div v-for="section in tributeVideoSections" :key="section">
                                     <small>{{ section | prettyText }}</small>
                                     <v-chip small :color="getColorFromValue($store.state.tributeVideo[section])" class="ml-1">
                                         {{ handleCustomValues(section, $store.state.tributeVideo[section], 'tributeVideo')}}
                                     </v-chip>
                                 </div>
                                <!-- <pre style="max-height: 30vh; overflow: auto;">{{ this.$store.state.tributeVideo }}</pre> -->
                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                    <v-expansion-panel v-for="renderType in ['standard', 'public']" :key="renderType">
                        <v-expansion-panel-header>
                            {{ renderType | prettyText }} Render
                            <div class="ml-1">
                                <v-chip small :color="getColorFromValue(handleCustomValues('status', tributeRender?.[`${renderType}Render`]?.status, 'render'))">
                                    <template>
                                        {{ handleCustomValues('status', tributeRender?.[`${renderType}Render`]?.status, 'render') || 'N/A' }}
                                    </template>
                                </v-chip>
                            </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <div class="debug-section gap" v-if="tributeRender?.[`${renderType}Render`]">
                                <!-- Tribute Render -->
                                <div v-for="section in renderSections" :key="`standard_${section}`">
                                     <small>{{ section | prettyText }}</small>
                                     <v-chip small :color="getColorFromValue(tributeRender?.[`${renderType}Render`][section])" class="ml-1">
                                         {{ handleCustomValues(section, tributeRender?.[`${renderType}Render`]?.[section], 'render')}}
                                     </v-chip>
                                 </div>
                            </div>
                            <div class="d-flex gap">
                                <!-- <pre style="max-height: 50vh; overflow: auto;">{{ tributeRender.standardRender }}</pre> -->
                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                    <!-- User Story / UX expectations visual breakdown -->
                    <v-expansion-panel>
                        <v-expansion-panel-header>
                            User Stories <v-chip small>last synced:{{ lastSyncedDate | prettyDate }}</v-chip>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content class="px-0">
                            <div>
                                <v-switch dense v-model="showAllValues" :true-value="false" :false-value="true"
                                    :label="showAllValues ? 'Showing all' : 'Showing only `truthy`'" class="m-0"/>
                            </div>
                            <div class="mb-1 py-1">
                                <strong>
                                    <v-icon>mdi-{{ userStory.icon || 'account' }}</v-icon>
                                    As a {{ userStory.user }}
                                </strong>
                            </div>
                            <v-expansion-panels>
                                <v-expansion-panel 
                                    v-for="(storyData, storyLabel) in userStory.stories" :key="`${userStory.user}_${storyLabel}`"
                                    v-if="showAllValues || storyData.isActive"
                                >
                                    <v-expansion-panel-header>
                                        <v-badge inline dot :color="storyData.isActive ? 'success' : 'error'"
                                            style="flex: 0 1;"
                                        />
                                        <span style="flex-grow: 1;">{{ storyLabel }}:</span>
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content class="pl-1">
                                        <div class="">
                                            <div 
                                                v-for="(sectionData, sectionLabel) in storyData.workflows" :key="`${userStory.user}_${storyData}_story_${sectionLabel}`"
                                                class="mb-1 ml-1 px-0" :class="{'active-block': (storyData.isActive && sectionData.isActive)}"
                                                v-if="showAllValues || (storyData.isActive && sectionData.isActive)"
                                            >
                                                <strong>
                                                    <v-badge inline dot :color="storyData.isActive && sectionData.isActive ? 'success' : 'error'"/>
                                                    {{ sectionLabel | prettyText }}
                                                </strong>
                                                <div class="debug-section gap px-0 ml-3" v-for="(section, label) in sectionData.state"
                                                    v-if="showAllValues || isTruthy(section)"
                                                >
                                                    <small class="user-story-block">
                                                        <v-badge inline 
                                                            class="story-badge"
                                                            :color="getColorFromValue(section)"
                                                            :dot="!['object', 'array'].includes(typeof section)"
                                                            :icon="['object', 'array'].includes(typeof section) ? 'mdi-chevron-down': null"
                                                        />
                                                        {{ label | prettyText }}
                                                    </small>
                                                    <div v-if="['object', 'array'].includes(typeof section)"
                                                        class="sub-sections ml-3"
                                                    >
                                                        <small class="user-story-block"
                                                            v-for="(subSection, subSectionLabel) in section" 
                                                            v-if="showAllValues || isTruthy(subSection)">
                                                            <v-badge inline dot :color="getColorFromValue(subSection)"/>
                                                            {{ subSectionLabel | prettyText }}
                                                        </small>
                                                    </div>
                                                </div>
                                                <!-- <div class="">
                                                    <pre style="max-height: 50vh; overflow: auto;">{{ sectionData.state }}</pre>
                                                </div> -->
                                            </div>
                                        </div>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </div>
        </div>
        <v-fab-transition>
            <v-btn
                style="bottom: 10px; z-index:20;"
                :color="showDebugPanel ? 'pink' : 'white'"
                :dark="showDebugPanel"
                absolute
                bottom
                left
                fab
                @click.capture.stop.prevent="showDebugPanel = !showDebugPanel"
            >
                <v-icon>{{showDebugPanel ? 'mdi-bug' : 'mdi-bug-outline'}}</v-icon>
            </v-btn>
        </v-fab-transition>
    </div>
</template>

<script>
import { tributeWorkflows, tributeWorkflowEnum, creatomateRenderStatuses } from '@/constants.js';
import { startCase } from 'lodash';
import moment from 'moment';
export default {
    inject: ['state', 'tributeRender'],
    data() {
        return {
            // last time permissions were synced with spreadsheet: https://docs.google.com/spreadsheets/d/1EH82EE33Ot70J04E6iQ6CFgeBO3WMweyollpymfJjqU/edit?gid=0#gid=0
            lastSyncedDate: '2025-07-08T16:01:06.213Z',
            pinned: true,
            showDebugPanel: false,
            showAllValues: false,
            tributeVideoSections: [
                'submissionsOpen',
                'deadline',
                'tributeWorkflow',
                'renderStarted',
                'renderCompleted',
                'audioRenderRequired',
                'totalSlidesCount',
                'maxItems',
                'maxSessionItems',
                'duplicateSlideCount',
                'similarSlideCount',
                'totalDuration',
                'incomingUploadCount',
                'ingestPipelineCount',
                'rotatingPhotos',
            ],
            renderSections: [
                'outOfSync',
                'status',
                'creatomateId',
                'creatomateErrorMessage',
            ],
        }
    },
    methods: {
        handleCustomValues(type, val, context) {
            switch (`${context ? context + '_' : ''}${type}`) {
                case 'tributeVideo_tributeWorkflow':
                    return tributeWorkflows.find((kind, value) => value === val)?.label || val;
                case 'tributeVideo_submissionsOpen':
                    return val ? 'open' : 'closed';
                case 'tributeVideo_deadline':
                    return !val ? 'none' : moment(val).format('LL');
                case 'render_status':
                    return creatomateRenderStatuses.find((kind, value) => value === val)?.label || val;
                case 'status':
                    return `${val}`;
                default:
                    return `${val}`;
            }
        },
        getColorFromValue(val) {
            if (val && ['object', 'array'].includes(typeof val)) {
                val = '[object Object]';
            }
            switch (`${val}`) { 
                case '0':
                case 'false':
                    return 'error';
                case '1':
                case 'true':
                case 'Succeeded':
                    return 'success';
                case 'null':
                case 'undefined':
                    return 'gray'
                case '[object Object]':
                    return '';
                default:
                    return 'primary';
            }
        },
        isTruthy(val) {
            if (val && ['object', 'array'].includes(typeof val)) {
                if (Array.isArray(val)) {
                    return val.some(v => !!v);
                }
                return Object.values(val).some(v => !!v);
            }
            return !!val;
        },
    },
    filters: {
        prettyText(val) {
            return startCase(val);
        },
        prettyDate(val) {
            const d = moment(val);
            if (d.isValid()) {
                return d.format('L');
            }
            return val;
        }
    },
    computed: {
        userStory() {
            if (this.state.isContributor) {
                return {
                    user: 'Contributor',
                    icon: 'account',
                    stories: {
                        'Submissions Open or Rendering + Edit Mode': {
                            isActive: [
                                this.state.isContributor,
                                this.$store.state.tributeVideo.submissionsOpen,
                                !this.tributeRender?.standardRender || this.state?.editMode
                            ].every((val) => val === true),
                            workflows: {
                                'all workflows': {
                                    isActive: this.$store.state.tributeVideo.submissionsOpen,
                                    state: {
                                        'Manage Slides (own only)': {
                                            add: 1,
                                            edit: 1,
                                            delete: 1,
                                            'view (read only)': 1 
                                        },
                                        'Set Theme': 0,
                                        'Manage Music': 0,
                                        'Summary page': 1,
                                        'Preview Player on Summary': 0,
                                    }
                                },
                            }
                        },
                        'Submissions Closed and Not Created': {
                            isActive:[
                                this.state.isContributor,
                                !this.$store.state.tributeVideo.submissionsOpen,
                                !this.tributeRender?.standardRender
                            ].every((val) => val === true),
                            workflows: {
                                'Family managed': {
                                    isActive: this.$store.state?.tributeVideo?.tributeWorkflow === tributeWorkflowEnum.FAMILY,
                                    state: {
                                        'Manage Slides (own only)': {
                                            add: 0,
                                            edit: 0,
                                            delete: 0,
                                            'view (read only)': 1
                                        },
                                        'Set Theme': 0,
                                        'Manage Music': 0,
                                        'Summary page': 1,
                                        'Preview Player on Summary': 0,
                                    }
                                },
                                'Hybrid': {
                                    isActive: this.$store.state?.tributeVideo?.tributeWorkflow === tributeWorkflowEnum.HYBRID,
                                    state: {
                                        'Manage Slides': {
                                            add: 1,
                                            edit: 1,
                                            delete: 0,
                                        },
                                        'Set Theme': 0,
                                        'Manage Music': 0,
                                        'Sumary page': 1,
                                        'Preview Player on Summary': 0,
                                    }
                                },
                                'Funeral Home led': {
                                    isActive: this.$store.state?.tributeVideo?.tributeWorkflow === tributeWorkflowEnum.FUNERAL_HOME,
                                    state: {
                                        'Manage Slides': {
                                            add: 1,
                                            edit: 1,
                                            delete: 0,
                                        },
                                        'Set Theme': 0,
                                        'Manage Music': 0,
                                        'Sumary page': 1,
                                        'Preview Player on Summary': 0,
                                    }
                                },
                            }
                        },
                        'Rendering ': {
                            isActive:[
                                this.state.isContributor,
                                !this.$store.state.tributeVideo.submissionsOpen,
                                this.$store.state.tributeVideo.renderStarted && !this.$store.state.tributeVideo.renderCompleted
                            ].every((val) => val === true),
                            workflows: {
                                'all workflows': {
                                    isActive: true,
                                    state: {
                                        'Manage Slides (own only)': {
                                            add: 0,
                                            edit: 0,
                                            delete: 0,
                                        },
                                        'Submissions Closed Modal on Manage Slides': 1,
                                        'Set Theme': 0,
                                        'Manage Music': 0,
                                        'Summary page': 1,
                                        'Preview Player on Summary': 0,
                                    }
                                },
                            }
                        },
                        'Submissions Closed + Final Video Created ': {
                            isActive:[
                                this.state.isContributor,
                                !this.$store.state.tributeVideo.submissionsOpen,
                                this.tributeRender.standardRender?.status === 2
                            ].every((val) => val === true),
                            workflows: {
                                'all workflows': {
                                    isActive: true,
                                    state: {
                                        'Manage Slides (own only)': {
                                            add: 0,
                                            edit: 0,
                                            delete: 0,
                                        },
                                        'Submissions Closed Modal on Manage Slides': 0,
                                        'Set Theme': 0,
                                        'Manage Music': 0,
                                        'Summary page': 0,
                                        'Preview Player on Summary': 0,
                                        'View Final Video': 1,
                                    }
                                },
                            }
                        },
                    }
                }
            }
            if (this.state.isFamily) {
                return {
                    user: 'Family',
                    icon: 'account-group',
                    stories: {
                        'Submissions Open or Rendering + Edit Mode': {
                            isActive: [
                                this.state.isFamily,
                                this.$store.state.tributeVideo.submissionsOpen,
                                !this.tributeRender?.standardRender || this.state?.editMode
                            ].every((val) => val === true),
                            workflows: {
                                'Family managed': {
                                    isActive: this.$store.state?.tributeVideo?.tributeWorkflow === tributeWorkflowEnum.FAMILY,
                                    state: {
                                        'Manage Slides': {
                                            add: 1,
                                            edit: 1,
                                            delete: 1,
                                        },
                                        'Set Theme': 1,
                                        'Manage Music': 1,
                                        'Summary page': 1,
                                        'Start Render': 1,
                                        'Preview Player on Summary': 1,
                                    }
                                },
                                'Hybrid': {
                                    isActive: this.$store.state?.tributeVideo?.tributeWorkflow === tributeWorkflowEnum.HYBRID,
                                    state: {
                                        'Manage Slides': {
                                            add: 1,
                                            edit: 1,
                                            delete: 1,
                                        },
                                        'Set Theme': 1,
                                        'Manage Music': 1,
                                        'Summary page': 1,
                                        'Start Render': 0,
                                        'Preview Player on Summary': 1,
                                    }
                                },
                                'Funeral Home led': {
                                    isActive: this.$store.state?.tributeVideo?.tributeWorkflow === tributeWorkflowEnum.FUNERAL_HOME,
                                    state: {
                                        'Manage Slides': {
                                            add: 1,
                                            edit: 1,
                                            delete: 1,
                                        },
                                        'Set Theme': 0,
                                        'Manage Music': 0,
                                        'Summary page': 1,
                                        'Start Render': 0,
                                        'Preview Player on Summary': 0,
                                    }
                                },
                            }
                        },
                        'Submissions Closed and Not Created': {
                            isActive:[
                                this.state.isFamily,
                                !this.$store.state.tributeVideo.submissionsOpen,
                                !this.tributeRender?.standardRender
                            ].every((val) => val === true),
                            workflows: {
                                'Family managed': {
                                    isActive: this.$store.state?.tributeVideo?.tributeWorkflow === tributeWorkflowEnum.FAMILY,
                                    state: {
                                        'Manage Slides': {
                                            add: 0,
                                            edit: 0,
                                            delete: 0,
                                            'view (read only)': 1
                                        },
                                        'Set Theme': 0,
                                        'Manage Music': 0,
                                        'Summary page': 1,
                                        'Explore Media': 0,
                                        'Preview Player on Summary': 0,
                                        'Submissions Closed Modal on Manage Slides': 1,
                                    }
                                },
                                'Hybrid': {
                                    isActive: this.$store.state?.tributeVideo?.tributeWorkflow === tributeWorkflowEnum.HYBRID,
                                    state: {
                                        'Manage Slides': {
                                            add: 0,
                                            edit: 0,
                                            delete: 0,
                                            'view (read only)': 1
                                        },
                                        'Set Theme': 0,
                                        'Manage Music': 0,
                                        'Sumary page': 1,
                                        'Explore Media': 0,
                                        'Preview Player on Summary': 0,
                                        'Submissions Closed Modal on Manage Slides': 1,
                                    }
                                },
                                'Funeral Home led': {
                                    isActive: this.$store.state?.tributeVideo?.tributeWorkflow === tributeWorkflowEnum.FUNERAL_HOME,
                                    state: {
                                        'Manage Slides': {
                                            add: 0,
                                            edit: 0,
                                            delete: 0,
                                            'view (read only)': 1
                                        },
                                        'Set Theme': 0,
                                        'Manage Music': 0,
                                        'Sumary page': 1,
                                        'Preview Player on Summary': 0,
                                        'Submissions Closed Modal on Manage Slides': 1,
                                    }
                                },
                            }
                        },
                        'Rendering': {
                            isActive:[
                                this.state.isFamily,
                                !this.$store.state.tributeVideo.submissionsOpen,
                                this.$store.state.tributeVideo.renderStarted && !this.$store.state.tributeVideo.renderCompleted
                            ].every((val) => val === true),
                            workflows: {
                                'all workflows': {
                                    isActive: true,
                                    state: {
                                        'Manage Slides': {
                                            add: 0,
                                            edit: 0,
                                            delete: 0,
                                            view: 1
                                        },
                                        'Submissions Closed Modal on Manage Slides': 1,
                                        'Set Theme': 0,
                                        'Manage Music': 0,
                                        'Summary page': 1,
                                        'Preview Player on Summary': 0,
                                    }
                                },
                            }
                        },
                        'Submissions Closed + Final Video Created ': {
                            isActive:[
                                this.state.isFamily,
                                !this.$store.state.tributeVideo.submissionsOpen,
                                this.tributeRender.standardRender?.status === 2
                            ].every((val) => val === true),
                            workflows: {
                                'all workflows': {
                                    isActive: true,
                                    state: {
                                        'Manage Slides': {
                                            add: 0,
                                            edit: 0,
                                            delete: 0,
                                            view: 1,
                                        },
                                        'Submissions Closed Modal on Manage Slides': 0,
                                        'Set Theme': 0,
                                        'Manage Music': 0,
                                        'Summary page': 0,
                                        'Final Video Tab': 1,
                                        'Explore Media': 1,
                                    }
                                },
                            }
                        },
                    }
                }
            }

            // Assumed Funeral Home or Admin
            return {
                user: 'Funeral Home',
                icon: 'account-supervisor',
                stories: {
                        'Submissions Open or Rendering + Edit Mode': {
                            isActive: [
                                !this.state.isFamily && !this.state.isContributor,
                                this.$store.state.tributeVideo.submissionsOpen,
                                !this.tributeRender?.standardRender || this.state?.editMode
                            ].every((val) => val === true),
                            workflows: {
                                'all workflows': {
                                    isActive: true,
                                    state: {
                                        'Set Deadline': 1,
                                        'Change Main Profile Photo': 1,
                                        'Manage Slides': {
                                            add: 1,
                                            edit: 1,
                                            delete: 1,
                                        },
                                        'Set Theme': 1,
                                        'Manage Music': 1,
                                        'Summary page': 1,
                                        'Start Render': 1,
                                        'Preview Player on Summary': 1,
                                        'Tribute Settings Page': 1,
                                    }
                                },
                            }
                        },
                        'Submissions Closed and Not Created': {
                            isActive:[
                                !this.state.isFamily && !this.state.isContributor,
                                !this.$store.state.tributeVideo.submissionsOpen,
                                !this.tributeRender?.standardRender
                            ].every((val) => val === true),
                            workflows: {
                                'all workflows': {
                                    isActive: true,
                                    state: {
                                        'Set Deadline': 0,
                                        'Change Main Profile Photo': 1,
                                        'Manage Slides': {
                                            add: 1,
                                            edit: 1,
                                            delete: 1,
                                        },
                                        'Set Theme': 1,
                                        'Manage Music': 1,
                                        'Summary page': 1,
                                        'Start Render': 1,
                                        'Preview Player on Summary': 1,
                                        'Tribute Settings Page': 1,
                                    },
                                },
                            }
                        },
                        'Rendering': {
                            isActive:[
                                !this.state.isFamily && !this.state.isContributor,
                                !this.$store.state.tributeVideo.submissionsOpen,
                                this.$store.state.tributeVideo.renderStarted && !this.$store.state.tributeVideo.renderCompleted
                            ].every((val) => val === true),
                            workflows: {
                                'all workflows': {
                                    isActive: true,
                                    state: {
                                        'Set Deadline': 0,
                                        'Change Main Profile Photo': 1,
                                        'Manage Slides': {
                                            'add *': 1,
                                            'edit *': 1,
                                            'delete *': 1,
                                            'view': 1
                                        },
                                        'Submissions Closed Modal on Manage Slides': 0,
                                        'Set Theme': 0,
                                        'Manage Music': 0,
                                        'Summary page': 1,
                                        'Start Render (re-render) *': 1,
                                        'Preview Player on Summary': 0,
                                        'Tribute Settings Page': 1,
                                    }
                                },
                            }
                        },
                        'Submissions Closed + Final Video Created ': {
                            isActive:[
                                !this.state.isFamily && !this.state.isContributor,
                                !this.$store.state.tributeVideo.submissionsOpen,
                                this.tributeRender.standardRender?.status === 2
                            ].every((val) => val === true),
                            workflows: {
                                'all workflows': {
                                    isActive: true,
                                    state: {
                                        'Set Deadline': 0,
                                        'Change Main Profile Photo': 1,
                                        'Manage Slides': {
                                            add: 0,
                                            edit: 0,
                                            delete: 0,
                                            view: 1,
                                        },
                                        'Submissions Closed Modal on Manage Slides': 0,
                                        'Set Theme': 0,
                                        'Manage Music': 0,
                                        'Summary page': 0,
                                        'Final Video Tab': 1,
                                        'Explore Media': 1,
                                    }
                                },
                            }
                        },
                    }
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.debug-info-panel {
    position: fixed;
    right: -280px;
    opacity: 0.6;
    padding: 1rem;
    border-radius: 1rem;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    top: 0;
    bottom: 0;
    margin: auto;
    height: 80vh;
    width: 320px;
    background: #fff;
    z-index: 12;
    transition: all cubic-bezier(0.075, 0.82, 0.165, 1) 400ms;
    
    &.pinned {
        right: 0 !important;
        opacity: 0.8;
    }

    &:hover {
        right: 0;
        opacity: 1;
    }
    .debug-info-panel-body {
        overflow-y: auto;
        height: 90%;
    }
}
.header-section {
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.debug-section {
    display: inline-flex;
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
    & > div  {
        display: flex;
        justify-content: space-between;
        width: 100%;
        align-items: center;
    }
}
.sub-sections {
    display: flex;
    flex-direction: column;
    align-items: flex-start !important;
}
.gap {
    gap: 10px;
}
.panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.user-story-block {
    display: inline-flex;
    align-items: center;
    line-height: 1;
    margin-bottom: 6px;
}
.inactive-block {
    background: #fde5e5;
    border: 2px solid rgb(201, 63, 74);
    border-radius: 5px;
}
.active-block {
    background: #fafffa;
    border: 2px solid #292;
    border-radius: 5px;
}
.story-badge {
    &::v-deep {
        .v-icon {
            color: black;
        }
    }
}
</style>